import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Wrench, RefreshCw, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminMaintenance() {
  const token = localStorage.getItem('admin_token');
  const [fixingCompanyTags, setFixingCompanyTags] = useState(false);
  const [fixResult, setFixResult] = useState(null);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [diagRunning, setDiagRunning] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [analysisRunning, setAnalysisRunning] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await axios.get(`${API}/api/admin-portal/companies`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCompanies(res.data.companies || []);
      } catch (e) { /* ignore */ }
    };
    fetchCompanies();
  }, [token]);

  const fixCompanyTags = async () => {
    setFixingCompanyTags(true);
    setFixResult(null);
    try {
      const res = await axios.post(`${API}/api/cdr/fix-company-tags`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFixResult(res.data);
      toast.success(`Fixed ${res.data.fixed} records, deleted ${res.data.duplicates_deleted} duplicates`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix company tags');
    } finally {
      setFixingCompanyTags(false);
    }
  };

  const purgeAndResync = async () => {
    setPurging(true);
    setPurgeResult(null);
    try {
      const res = await axios.post(`${API}/api/cdr/purge-and-resync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurgeResult(res.data);
      setConfirmPurge(false);
      toast.success(`Purged ${res.data.deleted} records. CDR sync reset — next sync will repopulate all data.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purge data');
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Maintenance</h1>
        <p className="text-slate-400 mt-1">Data maintenance and repair tools</p>
      </div>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg">CDR Data Maintenance</CardTitle>
          <CardDescription>Tools to fix and maintain call detail records</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Fix Company Tags */}
          <div className="flex items-start justify-between p-4 rounded-lg border border-slate-600 bg-slate-900/50">
            <div>
              <p className="font-medium text-sm text-white">Fix Company Tags</p>
              <p className="text-xs text-muted-foreground mt-1">
                Re-tags CDR records with the correct company based on their account code
                and removes duplicates.
              </p>
              {fixResult && (
                <p className="text-xs text-emerald-400 mt-2" data-testid="fix-result">
                  Fixed {fixResult.fixed} records, deleted {fixResult.duplicates_deleted} duplicates.
                </p>
              )}
            </div>
            <Button
              onClick={fixCompanyTags}
              disabled={fixingCompanyTags}
              variant="outline"
              size="sm"
              data-testid="fix-company-tags-btn"
            >
              {fixingCompanyTags ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Wrench className="w-4 h-4 mr-2" />}
              {fixingCompanyTags ? 'Fixing...' : 'Run Fix'}
            </Button>
          </div>

          {/* Purge & Re-sync */}
          <div className="flex items-start justify-between p-4 rounded-lg border border-red-900/50 bg-red-950/20">
            <div>
              <p className="font-medium text-sm text-white flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                Purge All CDR Data & Re-sync
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Deletes all call detail records and resets the sync position.
                The next CDR sync will repopulate all data from scratch with correct company tags.
              </p>
              {purgeResult && (
                <p className="text-xs text-emerald-400 mt-2" data-testid="purge-result">
                  Purged {purgeResult.deleted} records. Sync state reset. Run a CDR sync to repopulate.
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              {!confirmPurge ? (
                <Button
                  onClick={() => setConfirmPurge(true)}
                  disabled={purging}
                  variant="destructive"
                  size="sm"
                  data-testid="purge-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Purge Data
                </Button>
              ) : (
                <>
                  <p className="text-xs text-red-400">Are you sure?</p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setConfirmPurge(false)}
                      variant="outline"
                      size="sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={purgeAndResync}
                      disabled={purging}
                      variant="destructive"
                      size="sm"
                      data-testid="purge-confirm-btn"
                    >
                      {purging ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                      {purging ? 'Purging...' : 'Confirm Purge'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* CDR Sync Diagnostic */}
          <div className="p-4 rounded-lg border border-blue-900/50 bg-blue-950/20 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-sm text-white">CDR Sync Diagnostic</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Tests the billing feed API for a company to see exactly what data is available — without importing anything.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="bg-slate-900 border border-slate-600 text-white text-xs rounded px-2 py-1.5"
                  data-testid="diag-company-select"
                >
                  <option value="">Select company...</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <Button
                  onClick={async () => {
                    if (!selectedCompany) {
                      toast.error('Select a company first');
                      return;
                    }
                    setDiagRunning(true);
                    setDiagResult(null);
                    try {
                      const res = await axios.post(`${API}/api/cdr/sync-diagnostic?company_id=${selectedCompany}`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setDiagResult(res.data);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Diagnostic failed');
                    } finally {
                      setDiagRunning(false);
                    }
                  }}
                  disabled={diagRunning || !selectedCompany}
                  variant="outline"
                  size="sm"
                  data-testid="diag-btn"
                >
                  {diagRunning ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Wrench className="w-4 h-4 mr-2" />}
                  {diagRunning ? 'Testing...' : 'Run Diagnostic'}
                </Button>
              </div>
            </div>
            {diagResult && (
              <pre className="text-xs text-blue-300 bg-slate-900 p-3 rounded overflow-auto max-h-80" data-testid="diag-result">
                {JSON.stringify(diagResult, null, 2)}
              </pre>
            )}
          </div>

          {/* Admin CDR Sync */}
          <div className="p-4 rounded-lg border border-emerald-900/50 bg-emerald-950/20 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-sm text-white">Admin CDR Sync</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Trigger a CDR sync for any company directly from the admin portal. Shows full debug info.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="bg-slate-900 border border-slate-600 text-white text-xs rounded px-2 py-1.5"
                  data-testid="sync-company-select"
                >
                  <option value="">Select company...</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <Button
                  onClick={async () => {
                    if (!selectedCompany) {
                      toast.error('Select a company first');
                      return;
                    }
                    setSyncRunning(true);
                    setSyncResult(null);
                    try {
                      const res = await axios.post(`${API}/api/cdr/admin-sync?company_id=${selectedCompany}`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setSyncResult(res.data);
                      toast.success(res.data.message);
                      // Poll status every 5 seconds
                      const poll = setInterval(async () => {
                        try {
                          const status = await axios.get(`${API}/api/cdr/admin-sync-status?company_id=${selectedCompany}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setSyncResult(status.data);
                          if (status.data.sync_state?.status === 'complete' || status.data.sync_state?.status === 'error') {
                            clearInterval(poll);
                            setSyncRunning(false);
                            if (status.data.sync_state?.status === 'complete') {
                              toast.success(`Sync complete: ${status.data.sync_state.records_synced} records for ${status.data.company}`);
                            } else {
                              toast.error(`Sync error: ${status.data.sync_state?.error}`);
                            }
                          }
                        } catch (e) { clearInterval(poll); setSyncRunning(false); }
                      }, 5000);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Sync failed');
                      setSyncRunning(false);
                    }
                  }}
                  disabled={syncRunning || !selectedCompany}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="admin-sync-btn"
                >
                  {syncRunning ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  {syncRunning ? 'Syncing...' : 'Sync Now'}
                </Button>
              </div>
            </div>
            {syncResult && (
              <pre className="text-xs text-emerald-300 bg-slate-900 p-3 rounded overflow-auto max-h-80" data-testid="sync-result">
                {JSON.stringify(syncResult, null, 2)}
              </pre>
            )}
          </div>

          {/* CDR Data Analysis */}
          <div className="p-4 rounded-lg border border-amber-900/50 bg-amber-950/20 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-sm text-white">CDR Data Analysis</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Analyze stored CDR data — shows call type breakdown, direction, date range for a company.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="bg-slate-900 border border-slate-600 text-white text-xs rounded px-2 py-1.5"
                >
                  <option value="">Select company...</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <Button
                  onClick={async () => {
                    if (!selectedCompany) { toast.error('Select a company first'); return; }
                    setAnalysisRunning(true);
                    setAnalysisResult(null);
                    try {
                      const res = await axios.post(`${API}/api/cdr/data-analysis?company_id=${selectedCompany}`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setAnalysisResult(res.data);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Analysis failed');
                    } finally {
                      setAnalysisRunning(false);
                    }
                  }}
                  disabled={analysisRunning || !selectedCompany}
                  variant="outline"
                  size="sm"
                  data-testid="analysis-btn"
                >
                  {analysisRunning ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Wrench className="w-4 h-4 mr-2" />}
                  {analysisRunning ? 'Analyzing...' : 'Analyze Data'}
                </Button>
              </div>
            </div>
            {analysisResult && (
              <pre className="text-xs text-amber-300 bg-slate-900 p-3 rounded overflow-auto max-h-80" data-testid="analysis-result">
                {JSON.stringify(analysisResult, null, 2)}
              </pre>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
